/**
 * The extraction strip (docs/ux/loi.md): the mode chip, the run sentence
 * with its confidence histogram, the warnings count, and Re-extract; or the
 * empty, extracting and failed variants. Never a usage, token or cost field.
 */

import { useState } from 'react';
import type { LoiView } from '@/app/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { longDate } from '@/lib/format';
import { StateChip } from '../casefile/ReviewBar';

export type ExtractState = 'none' | 'ready' | 'extracting' | 'failed';

export function extractStateOf(view: LoiView): ExtractState {
  const job = view.head.extract?.state;
  if (job === 'queued' || job === 'running') return 'extracting';
  if (job === 'failed') return 'failed';
  return view.extraction ? 'ready' : 'none';
}

const clock = (iso: string): string => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false });

export function ExtractionPanel({ matterRef, view, state, recorded, offline, approved, onExtract, onReextract, busy, stalled, onCheckNow }: {
  matterRef: string;
  view: LoiView;
  state: ExtractState;
  recorded: boolean;
  offline: boolean;
  approved: boolean;
  onExtract: () => void;
  onReextract: () => void;
  busy: boolean;
  stalled: boolean;
  onCheckNow: () => void;
}) {
  const [warningsOpen, setWarningsOpen] = useState(false);
  const e = view.extraction;
  const s = view.head.summary;
  const withParts = view.questions.filter((q) => q.hasChildren).length;
  const pages = view.letterPages ?? e?.sourcePages.length ?? 0;

  return (
    <section aria-label="Extraction" data-slot="extraction-panel" data-extract-state={state} className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper px-4 py-2 text-[13px]">
      <div className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-2">
        {state === 'extracting' ? <StateChip label="Extracting" tone="neutral" /> : state === 'failed' ? <StateChip label="Extraction failed" tone="error" /> : (
          <Badge variant="outline" className={recorded ? 'h-6 border-transparent bg-beam-warning-bg px-2.5 text-xs text-beam-warning' : 'h-6 px-2.5 text-xs'}>{state === 'none' ? (recorded ? 'LOI: recorded' : 'LOI: live') : recorded ? 'Recorded extraction' : 'Live extraction'}</Badge>
        )}
        {state === 'none' ? (
          <span>No extraction has been run for {matterRef}. The letter of instruction ({pages} {pages === 1 ? 'page' : 'pages'}) is on file.</span>
        ) : state === 'extracting' ? (
          <span role="status" className="min-w-0 flex-1">
            {recorded ? 'Reading the letter: serving the recorded result for this letter (LOI: recorded).' : `Reading the letter: ${pages} pages through Textract, then one Bedrock call with the form's schema (${e?.profile ?? 'au.*'}, ap-southeast-2).`}
            {stalled ? <> Checking the extraction is delayed. Retrying. <button type="button" className="text-beam-signature underline-offset-4 hover:underline" onClick={onCheckNow}>Check now</button></> : null}
          </span>
        ) : state === 'failed' ? (
          <div role="alert" className="flex min-w-0 flex-1 flex-wrap items-center gap-3 rounded-lg border border-l-[3px] border-beam-line border-l-beam-error px-3 py-1.5">
            <span>{e ? `The new extraction did not complete. The extraction under review (run ${e.runId.slice(0, 8)}) and its decisions are unchanged.` : 'The extraction did not complete. The letter is unchanged and nothing was written.'}</span>
            <Button variant="outline" size="lg" className="h-9" disabled={offline} aria-disabled={busy ? 'true' : undefined} onClick={() => { if (!busy) onExtract(); }}>{offline ? 'Paused while offline' : busy ? 'Starting' : 'Run extraction again'}</Button>
          </div>
        ) : e ? (
          <span className="min-w-0 flex-1">
            Extraction run{' '}
            <Tooltip>
              <TooltipTrigger asChild><span tabIndex={0} className="tabular cursor-default rounded-sm underline decoration-dotted underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/50">{e.runId.slice(0, 8)}</span></TooltipTrigger>
              <TooltipContent className="max-w-xs"><p className="tabular">{e.runId}</p><p>{e.model === 'recorded' ? 'Recorded result' : e.profile}. Source pages {e.sourcePages.join(', ') || 'unknown'}.</p></TooltipContent>
            </Tooltip>
            , {longDate(e.at)} at {clock(e.at)}: <span className="tabular">{pages}</span> pages, <span className="tabular">{s?.fields ?? view.fields.length}</span> fields, <span className="tabular">{view.tree.length}</span> questions{withParts > 0 ? ` (${withParts} with parts)` : ''}, <span className="tabular">{view.standingInstructions.length}</span> standing {view.standingInstructions.length === 1 ? 'instruction' : 'instructions'}.
            {s ? <> Confidence: <span className="tabular">{s.high}</span> high, <span className="tabular">{s.medium}</span> medium, <span className="tabular">{s.low}</span> low.</> : null}
            {e.warnings.length > 0 ? <> <button type="button" aria-expanded={warningsOpen} className="text-beam-signature underline-offset-4 hover:underline" onClick={() => setWarningsOpen((v) => !v)}>{e.warnings.length} {e.warnings.length === 1 ? 'warning' : 'warnings'}</button></> : null}
          </span>
        ) : null}
        {state === 'ready' ? (
          <span className="ml-auto">
            <Button variant="outline" size="lg" className="h-9" disabled={offline} aria-disabled={busy ? 'true' : undefined} aria-describedby={approved ? 'reextract-note' : undefined} onClick={() => { if (!busy) onReextract(); }}>{offline ? 'Paused while offline' : busy ? 'Starting' : 'Re-extract'}</Button>
            {approved ? <span id="reextract-note" className="sr-only">Re-extraction after approval reopens the review; the approved version stays on file.</span> : null}
          </span>
        ) : null}
      </div>
      {state === 'extracting' ? (
        <div aria-hidden="true" className="relative h-1 w-full overflow-hidden rounded-full bg-beam-cream-deep">
          <div className="absolute inset-y-0 w-1/3 rounded-full bg-beam-ink motion-safe:animate-[beam-indeterminate_1.4s_ease-in-out_infinite] motion-reduce:w-full motion-reduce:bg-beam-ink/40" />
        </div>
      ) : null}
      {warningsOpen && e ? <ul className="list-disc pl-5 text-xs text-beam-text-muted">{e.warnings.map((w) => <li key={w}>{w}</li>)}</ul> : null}
    </section>
  );
}
